import React, { useEffect, useRef, useState } from 'react';

/**
 * CityPicker — "choose the city you're in" for a public signal. Regular Party
 * Resume (brutalist) restyle of the Portal city picker: a selected-city chip,
 * a Mapbox "search any city" box, and a nearest-cities quick-pick from the
 * shared catalog (ordered by proximity when we know where you are).
 *
 * Single value: onChange({ slug, label, lat, lng }) or onChange(null) to clear.
 */
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

function slugify(text) {
  return (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function distanceKm(aLat, aLng, bLat, bLng) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

/**
 * Mapbox place search → `[{ slug, label, lat, lng }]`. Exported so the admin audience editor can
 * offer "any city" with the same shape the catalog uses; empty without a token.
 */
export async function geocodeCities(q) {
  const term = (q || '').trim();
  if (!term || !MAPBOX_TOKEN) return [];
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(term)}.json`
    + `?types=place,locality&limit=6&language=en&access_token=${MAPBOX_TOKEN}`;
  const data = await (await fetch(url)).json();
  return (data.features || []).map((f) => {
    const text = f.text || '';
    const ctx = f.context || [];
    const region = ctx.find((c) => (c.id || '').startsWith('region.'))?.text;
    const country = ctx.find((c) => (c.id || '').startsWith('country.'))?.text;
    const label = [text, region, country].filter(Boolean).join(', ');
    const slug = slugify([text, region || country].filter(Boolean).join('-'));
    const [lng, lat] = f.center || [];
    return { slug, label, lat, lng };
  }).filter((r) => r.slug);
}

export default function CityPicker({ value, onChange, cities = [], nearLocation = null }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [changing, setChanging] = useState(false); // reveal search again after a pick
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (!q || !MAPBOX_TOKEN) { setResults([]); return undefined; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json`
          + `?types=place,locality&limit=6&language=en&access_token=${MAPBOX_TOKEN}`;
        const data = await (await fetch(url)).json();
        const items = (data.features || []).map((f) => {
          const text = f.text || '';
          const ctx = f.context || [];
          const region = ctx.find((c) => (c.id || '').startsWith('region.'))?.text;
          const country = ctx.find((c) => (c.id || '').startsWith('country.'))?.text;
          const label = [text, region, country].filter(Boolean).join(', ');
          const slug = slugify([text, region || country].filter(Boolean).join('-'));
          const [lng, lat] = f.center || [];
          return { slug, label, lat, lng };
        }).filter((r) => r.slug);
        setResults(items);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  const pick = (city) => {
    onChange?.(city);
    setQuery('');
    setResults([]);
    setOpen(false);
    setChanging(false);
    inputRef.current?.blur();
  };

  // Nearest-first when we know the user's location, else the catalog order.
  let quickList = cities.filter((c) => !value || c.slug !== value.slug);
  if (nearLocation?.lat != null && nearLocation?.lng != null) {
    quickList = quickList
      .map((c) => ({ ...c, _d: (typeof c.lat === 'number' && typeof c.lng === 'number')
        ? distanceKm(nearLocation.lat, nearLocation.lng, c.lat, c.lng) : Infinity }))
      .sort((a, b) => a._d - b._d);
  }

  const searchBox = (
    <div className="relative">
      <input
        ref={inputRef}
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search any city…"
        className="w-full border-2 border-black bg-white px-4 py-3 font-mono text-sm text-black placeholder-gray-400 outline-none focus:ring-0"
      />
      {open && (results.length > 0 || searching || query.trim()) && (
        <div className="absolute inset-x-0 top-full z-20 mt-1 max-h-64 overflow-y-auto border-2 border-black bg-white">
          {searching && results.length === 0 ? (
            <p className="px-3 py-3 font-mono text-[11px] uppercase tracking-wide text-gray-400">Searching…</p>
          ) : results.length === 0 ? (
            <p className="px-3 py-3 font-mono text-[11px] uppercase tracking-wide text-gray-400">No results</p>
          ) : (
            results.map((r) => (
              <button
                key={r.slug}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(r); }}
                className="block w-full border-b border-gray-100 px-3 py-2.5 text-left font-mono text-[12px] uppercase tracking-wide last:border-b-0 hover:bg-gray-50"
              >
                {r.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );

  return (
    <div>
      {value && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => onChange?.(null)}
            className="inline-flex items-center gap-2 border-2 border-black bg-black px-3 py-2 font-mono text-xs font-bold uppercase tracking-wide text-white hover:opacity-90"
            title="Remove"
          >
            {(value.label || '').split(',')[0]} ×
          </button>
        </div>
      )}

      {value && !changing ? (
        <button
          type="button"
          onClick={() => { setChanging(true); setTimeout(() => inputRef.current?.focus(), 0); }}
          className="border-2 border-black bg-white px-4 py-2.5 font-mono text-[11px] font-bold uppercase tracking-wider text-black hover:bg-black hover:text-white"
        >
          Change city →
        </button>
      ) : (
        <>
          <div className="mb-3">{searchBox}</div>
          {quickList.length > 0 && (
            <>
              <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">
                {nearLocation?.lat != null ? 'Nearest cities' : 'Quick pick'}
              </p>
              <div className="flex max-h-48 flex-wrap gap-2 overflow-y-auto">
                {quickList.map((c) => (
                  <button
                    key={c.slug}
                    type="button"
                    onClick={() => pick({ slug: c.slug, label: c.label, lat: c.lat, lng: c.lng })}
                    className="border-2 border-black bg-white px-3 py-2 font-mono text-[11px] font-bold uppercase tracking-wide text-black hover:bg-black hover:text-white"
                  >
                    {(c.label || '').split(',')[0]}
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
